import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AskAgentAction } from '@/components/ui/ask-agent-action'
import { Card, CardContent } from '@/components/ui/card'
import type { BridgeInsight } from '@/services/api/graph'
import { SectionHeader } from '../primitives/SectionHeader'
import type { BridgeGroup } from '../selectors'
import { BridgeGroupCard } from './BridgeGroupCard'

export interface BridgesCardProps {
  groups: BridgeGroup[]
  totalBridges: number
  hasClusters: boolean
  labelFor: (compId: number) => string
  onBridgeClick?: (bridge: BridgeInsight) => void
  onOpenDocument?: (documentId: string) => void
  onSeeAll?: () => void
}

export function BridgesCard({
  groups,
  totalBridges,
  hasClusters,
  labelFor,
  onBridgeClick,
  onOpenDocument,
  onSeeAll,
}: BridgesCardProps) {
  const { t } = useTranslation()
  return (
    <Card size="compact" className="p-4">
      <SectionHeader
        title="Cross-cluster bridges"
        subLabel="Grouped by the two clusters each bridge joins"
        guide={{
          to: '/docs/investigation-analysis#cross-cluster-bridges',
          hint: 'Bridges are direct edges that connect entities across different Louvain clusters.',
        }}
        actions={
          onSeeAll && totalBridges > 0 ? (
            <button
              type="button"
              onClick={onSeeAll}
              className="text-xs text-[var(--primary)] font-medium hover:underline inline-flex items-center gap-1"
            >
              See all {totalBridges} bridges <ArrowRight className="size-3" />
            </button>
          ) : null
        }
      />
      {groups.length === 0 ? (
        <Card variant="dashed" size="compact" className="p-6 text-center">
          <CardContent className="px-0">
            <p className="text-xs text-muted-foreground">
              {hasClusters
                ? 'No cross-cluster bridges found — your case graph is well-connected within each cluster.'
                : 'Run Recompute to detect bridges between clusters.'}
            </p>
            <AskAgentAction
              variant="compact"
              prompt="Need help analyzing bridges?"
              label={t('investigations.graph.toolbar.askSpectre')}
              className="mt-3 justify-center"
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-2.5">
          {groups.map(g => (
            <BridgeGroupCard
              key={g.key}
              group={g}
              labelA={labelFor(g.componentA)}
              labelB={labelFor(g.componentB)}
              onBridgeClick={onBridgeClick}
              onOpenDocument={onOpenDocument}
            />
          ))}
        </div>
      )}
    </Card>
  )
}
